import { Calendar, Mail01 } from '@untitledui/icons';
import { AnimatePresence, motion } from 'motion/react';
import { useEffect, useState } from 'react';

import { cx } from '@/utils/cx';

export interface ActivityItem {
  type: 'email' | 'calendar';
  name: string;
  detail: string;
}

interface ActivityFeedProps {
  items: ActivityItem[];
  className?: string;
  /** Interval in ms between item rotations (default: 2000) */
  rotationInterval?: number;
}

export function ActivityFeed({
  items,
  className,
  rotationInterval = 2000,
}: ActivityFeedProps) {
  const [currentIndex, setCurrentIndex] = useState(0);

  // Rotate through items
  useEffect(() => {
    if (items.length <= 1) {
      return;
    }

    const interval = setInterval(() => {
      setCurrentIndex(prev => (prev + 1) % items.length);
    }, rotationInterval);

    return () => clearInterval(interval);
  }, [items.length, rotationInterval]);

  // Reset index if items change and index is out of bounds
  useEffect(() => {
    if (currentIndex >= items.length) {
      setCurrentIndex(0);
    }
  }, [items.length, currentIndex]);

  if (items.length === 0) {
    return null;
  }

  // Compute safe index at render time to prevent out-of-bounds access
  // when items array shrinks before the effect resets currentIndex
  const safeIndex = currentIndex < items.length ? currentIndex : 0;
  const item = items[safeIndex];
  const Icon = item.type === 'email' ? Mail01 : Calendar;
  const prefix = item.type === 'email' && item.name ? `${item.name}: ` : '';

  return (
    <div aria-hidden='true' className={cx('h-5 overflow-hidden', className)}>
      <AnimatePresence mode='wait'>
        <motion.div
          key={`${item.type}-${item.name}-${item.detail}-${safeIndex}`}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.3, ease: 'easeInOut' }}
          className='text-sm text-tertiary truncate'
        >
          <Icon
            className='size-3.5 shrink-0 inline-block align-text-bottom'
            aria-hidden='true'
          />{' '}
          {prefix}
          {item.detail}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
